import React from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'

import { formatBlogDate } from '@/utils/date'
import FeaturesFooter from '@/components/sections/FeaturesFooter'
import { Columns, Column, Layout, Row, Rows, Section } from '@/components/layout'
import BlogNav from '@/components/blocks/BlogNav'
import BlogArticleListing from '@/components/blocks/BlogArticleListing'
import BlogArticleListingSmall from '@/components/blocks/BlogArticleListingSmall'
import BlogArticleListingSmallHeader from '@/components/blocks/BlogArticleListingSmallHeader'
import BlogSubscribe from '@/components/blocks/BlogSubscribe'
import { getFeaturedPosts, getRecentPosts, getPopularPosts, getBlogConfig } from '@/lib/blog/post'

import styles from './index.module.scss'

const pageId = 'blog'

export default function Blog({ featuredPosts, popularPosts, recentPosts }) {
  const { formatMessage } = useIntl()
  return (
    <Layout.Content>
      <Section bgBlob textCenter>
        <h1>{formatMessage({ id: `pages.${pageId}.title` })}</h1>
        <p>{formatMessage({ id: `pages.${pageId}.description` })}</p>
        <BlogNav />
      </Section>
      <Section>
        <h4>{formatMessage({ id: `pages.${pageId}.featuredArticles` })}</h4>
        <Columns className={styles.grid} gap={24} wrap>
          {featuredPosts.map((post, index) => (
            <BlogArticleListing
              key={post.slug}
              categories={post.categories?.edges?.map(({ node: category }) => ({ ...category, link: `/blog/category/${category.slug}` }))}
              date={formatBlogDate(new Date(post.date))}
              id={`featured-${post.title}-${index}`}
              img={post.featuredImage?.node?.sourceUrl}
              imgAlt={post.featuredImage?.node?.altText}
              link={`/blog/${post.slug}`}
              title={post.title}
            />
          ))}
        </Columns>
      </Section>

      <Section>
        <Columns>
          <Column>
            <Rows gap={0}>
              <BlogArticleListingSmallHeader title={formatMessage({ id: `pages.${pageId}.popularArticles` })} />
              {popularPosts.map((post, index) => (
                <BlogArticleListingSmall
                  key={post.slug}
                  date={formatBlogDate(new Date(post.date))}
                  id={`featured-${post.title}-${index}`}
                  img={post.featuredImage?.node?.sourceUrl}
                  imgAlt={post.featuredImage?.node?.altText}
                  link={`/blog/${post.slug}`}
                  title={post.title}
                />
              ))}
            </Rows>
          </Column>
          <Column>
            <Rows gap={0}>
              <BlogArticleListingSmallHeader title={formatMessage({ id: `pages.${pageId}.recentArticles` })} />
              {recentPosts.map((post, index) => (
                <BlogArticleListingSmall
                  key={post.slug}
                  date={formatBlogDate(new Date(post.date))}
                  id={`featured-${post.title}-${index}`}
                  img={post.featuredImage?.node?.sourceUrl}
                  imgAlt={post.featuredImage?.node?.altText}
                  link={`/blog/${post.slug}`}
                  title={post.title}
                />
              ))}
            </Rows>
          </Column>
        </Columns>

        <Row center>
          <BlogSubscribe />
        </Row>
      </Section>

      <FeaturesFooter />

    </Layout.Content>
  )
}

export async function getStaticProps(ctx) {
  if (ctx.params.index) return { notFound: true }
  const {
    featured_posts: featuredPostSlugs,
    popular_posts: popularPostSlugs,
    recent_posts: recentPostSlugs,
    revalidate_seconds: revalidateTime,
  } = await getBlogConfig()

  const featuredPosts = await getFeaturedPosts(featuredPostSlugs)
  const popularPosts = await getPopularPosts(popularPostSlugs)
  const recentPosts = await getRecentPosts(recentPostSlugs)

  return {
    props: {
      featuredPosts: featuredPosts || [],
      popularPosts: popularPosts || [],
      recentPosts: recentPosts || [],
    },
    revalidate: revalidateTime,
  }
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

Blog.propTypes = {
  featuredPosts: PropTypes.arrayOf(PropTypes.shape({})),
  popularPosts: PropTypes.arrayOf(PropTypes.shape({})),
  recentPosts: PropTypes.arrayOf(PropTypes.shape({})),
}

Blog.layoutProps = {
  pageId,
}
